import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import {
    ProcurementLine,
    ProcurementLineUpdate,
    ProcurementResponse,
    ProcurementStatus,
    ProcurementSummary,
    Vendor,
} from '../types';
import { procurementApi } from '../services/api';
import { getIsAdmin } from '../utils/auth';
import { formatCurrency } from '../utils/costing';

interface ProcurementPageProps {
    rfqId: string;
}

const STATUS_LABEL: Record<ProcurementStatus, string> = {
    [ProcurementStatus.PENDING]: 'Pending',
    [ProcurementStatus.QUOTED]: 'Quoted',
    [ProcurementStatus.ORDERED]: 'Ordered',
    [ProcurementStatus.RECEIVED]: 'Received',
};

const STATUS_PILL: Record<ProcurementStatus, string> = {
    [ProcurementStatus.PENDING]: 'bg-[#141414]/10 text-[#141414]/70',
    [ProcurementStatus.QUOTED]: 'bg-[#1e5f8f]/15 text-[#1e5f8f]',
    [ProcurementStatus.ORDERED]: 'bg-[#e0461e]/15 text-[#e0461e]',
    [ProcurementStatus.RECEIVED]: 'bg-[#4a6b3d]/15 text-[#4a6b3d]',
};

function lineKey(line: ProcurementLine): string {
    return line.equipment_id;
}

function extractErrorDetail(err: unknown, fallback: string): string {
    if (axios.isAxiosError(err)) {
        const detail = err.response?.data?.detail;
        if (typeof detail === 'string') return detail;
        return err.message ?? fallback;
    }
    if (err instanceof Error) return err.message;
    return fallback;
}

const SummaryTiles: React.FC<{ summary: ProcurementSummary }> = ({ summary }) => {
    const delta = summary.actual_total - summary.expected_total;
    const deltaSign = delta > 0 ? '+' : delta < 0 ? '−' : '';
    const deltaClass = delta > 0 ? 'text-[#c2251a]' : delta < 0 ? 'text-[#4a6b3d]' : 'text-[#141414]/55';
    return (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <Tile
                label="Ordered"
                value={`${summary.ordered_lines} / ${summary.total_lines}`}
                hint="lines"
            />
            <Tile
                label="Received"
                value={`${summary.received_lines} / ${summary.total_lines}`}
                hint="lines"
            />
            <Tile label="Expected spend" value={formatCurrency(summary.expected_total)} />
            <Tile
                label="Actual spend"
                value={formatCurrency(summary.actual_total)}
                hint={
                    summary.actual_total > 0 || summary.expected_total > 0 ? (
                        <span className={deltaClass}>
                            {deltaSign}
                            {formatCurrency(Math.abs(delta))} vs expected
                        </span>
                    ) : undefined
                }
            />
        </div>
    );
};

const Tile: React.FC<{
    label: string;
    value: string;
    hint?: React.ReactNode;
}> = ({ label, value, hint }) => (
    <div className="border border-[#141414]/20 bg-white px-4 py-3">
        <div className="ff-mono text-[10px] uppercase tracking-[0.2em] text-[#141414]/55">
            {label}
        </div>
        <div className="ff-serif text-2xl leading-none mt-1">{value}</div>
        {hint !== undefined && (
            <div className="ff-mono text-[10px] text-[#141414]/55 mt-1.5">{hint}</div>
        )}
    </div>
);

const ProcurementPage: React.FC<ProcurementPageProps> = ({ rfqId }) => {
    const isAdmin = getIsAdmin();
    const [response, setResponse] = useState<ProcurementResponse | null>(null);
    const [vendors, setVendors] = useState<Vendor[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [savingKey, setSavingKey] = useState<string | null>(null);

    const load = useCallback(async () => {
        setIsLoading(true);
        setError(null);
        try {
            const [procurement, vendorList] = await Promise.all([
                procurementApi.get(rfqId),
                procurementApi.listVendors(),
            ]);
            setResponse(procurement);
            setVendors(vendorList);
        } catch (err) {
            setError(extractErrorDetail(err, 'Failed to load procurement'));
        } finally {
            setIsLoading(false);
        }
    }, [rfqId]);

    useEffect(() => {
        void load();
    }, [load]);

    const persist = useCallback(
        async (
            line: ProcurementLine,
            patch: ProcurementLineUpdate
        ): Promise<ProcurementLine | null> => {
            const key = lineKey(line);
            setSavingKey(key);
            setError(null);
            try {
                const updated = await procurementApi.update(rfqId, line.equipment_id, patch);
                // Re-fetch so summary + orphans stay in sync with server truth.
                const fresh = await procurementApi.get(rfqId);
                setResponse(fresh);
                return updated;
            } catch (err) {
                setError(extractErrorDetail(err, 'Failed to save procurement line'));
                return null;
            } finally {
                setSavingKey(null);
            }
        },
        [rfqId]
    );

    const handleRemoveOrphan = useCallback(
        async (line: ProcurementLine) => {
            const key = lineKey(line);
            setSavingKey(key);
            setError(null);
            try {
                await procurementApi.remove(rfqId, line.equipment_id);
                const fresh = await procurementApi.get(rfqId);
                setResponse(fresh);
            } catch (err) {
                setError(extractErrorDetail(err, 'Failed to remove procurement line'));
            } finally {
                setSavingKey(null);
            }
        },
        [rfqId]
    );

    const lines = useMemo(() => response?.lines ?? [], [response]);
    const activeLines = useMemo(() => lines.filter((l) => !l.is_orphaned), [lines]);
    const orphanLines = useMemo(() => lines.filter((l) => l.is_orphaned), [lines]);

    if (isLoading) {
        return (
            <div className="ff-mono text-sm text-[#141414]/55 py-8">
                Loading procurement…
            </div>
        );
    }

    return (
        <div>
            {error && (
                <div className="mb-4 border border-[#c2251a] bg-[#c2251a]/10 text-[#c2251a] px-4 py-3 ff-mono text-sm">
                    {error}
                </div>
            )}

            {response && <SummaryTiles summary={response.summary} />}

            {activeLines.length === 0 ? (
                <div className="border border-dashed border-[#141414]/25 bg-white px-6 py-10 text-center">
                    <div className="ff-serif text-2xl leading-none">
                        No BOM yet
                    </div>
                    <p className="ff-mono text-xs text-[#141414]/55 mt-2">
                        Add equipment to conveyor stations to build the bill of materials.
                    </p>
                </div>
            ) : (
                <ProcurementTable
                    lines={activeLines}
                    vendors={vendors}
                    isAdmin={isAdmin}
                    savingKey={savingKey}
                    onPersist={persist}
                />
            )}

            {orphanLines.length > 0 && (
                <div className="mt-8">
                    <div className="ff-mono text-[10px] uppercase tracking-[0.2em] text-[#141414]/55 mb-2">
                        Removed from line
                    </div>
                    <p className="ff-mono text-[11px] text-[#141414]/55 mb-3">
                        Equipment that had procurement activity but is no longer in any
                        station. Review before removing — something may already be
                        ordered.
                    </p>
                    <OrphanTable
                        lines={orphanLines}
                        vendors={vendors}
                        isAdmin={isAdmin}
                        savingKey={savingKey}
                        onRemove={handleRemoveOrphan}
                    />
                </div>
            )}
        </div>
    );
};

interface TableProps {
    lines: ProcurementLine[];
    vendors: Vendor[];
    isAdmin: boolean;
    savingKey: string | null;
    onPersist: (
        line: ProcurementLine,
        patch: ProcurementLineUpdate
    ) => Promise<ProcurementLine | null>;
}

const ProcurementTable: React.FC<TableProps> = ({
    lines,
    vendors,
    isAdmin,
    savingKey,
    onPersist,
}) => {
    return (
        <div className="border border-[#141414]/20 bg-white overflow-x-auto">
            <table className="w-full ff-mono text-xs">
                <thead className="bg-[#141414]/[0.04] border-b border-[#141414]/15">
                    <tr className="text-left">
                        <Th>Equipment</Th>
                        <Th className="text-right">Qty</Th>
                        <Th className="text-right">Expected</Th>
                        <Th>Vendor</Th>
                        <Th>PO #</Th>
                        <Th>Status</Th>
                        <Th className="text-right">Actual unit</Th>
                        <Th>Delivery</Th>
                        <Th>Notes</Th>
                    </tr>
                </thead>
                <tbody>
                    {lines.map((line) => (
                        <ProcurementRow
                            key={lineKey(line)}
                            line={line}
                            vendors={vendors}
                            isAdmin={isAdmin}
                            isSaving={savingKey === lineKey(line)}
                            onPersist={onPersist}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    );
};

const Th: React.FC<{ children: React.ReactNode; className?: string }> = ({
    children,
    className,
}) => (
    <th
        className={`ff-mono text-[10px] uppercase tracking-[0.15em] text-[#141414]/55 font-normal px-3 py-2 ${
            className ?? ''
        }`}
    >
        {children}
    </th>
);

interface RowProps {
    line: ProcurementLine;
    vendors: Vendor[];
    isAdmin: boolean;
    isSaving: boolean;
    onPersist: (
        line: ProcurementLine,
        patch: ProcurementLineUpdate
    ) => Promise<ProcurementLine | null>;
}

const ProcurementRow: React.FC<RowProps> = ({
    line,
    vendors,
    isAdmin,
    isSaving,
    onPersist,
}) => {
    const [poDraft, setPoDraft] = useState<string>(line.po_number ?? '');
    const [actualDraft, setActualDraft] = useState<string>(
        line.actual_unit_cost != null ? String(line.actual_unit_cost) : ''
    );
    const [deliveryDraft, setDeliveryDraft] = useState<string>(line.expected_delivery ?? '');
    const [notesDraft, setNotesDraft] = useState<string>(line.notes ?? '');
    const lastSynced = useRef<string>(lineKey(line));

    useEffect(() => {
        // If the incoming line changes identity (unlikely here) or values
        // shift from elsewhere, resync drafts.
        const key = lineKey(line);
        if (lastSynced.current !== key) {
            lastSynced.current = key;
        }
        setPoDraft(line.po_number ?? '');
        setActualDraft(line.actual_unit_cost != null ? String(line.actual_unit_cost) : '');
        setDeliveryDraft(line.expected_delivery ?? '');
        setNotesDraft(line.notes ?? '');
    }, [line]);

    const commitPatch = async (patch: ProcurementLineUpdate) => {
        await onPersist(line, patch);
    };

    const readOnly = !isAdmin;

    return (
        <tr className="border-b border-[#141414]/10 last:border-b-0 align-top">
            <td className="px-3 py-2">
                <div className="text-[#141414]">{line.equipment_name}</div>
                <div className="text-[10px] text-[#141414]/45">{line.equipment_id}</div>
            </td>
            <td className="px-3 py-2 text-right tabular-nums">{line.quantity}</td>
            <td className="px-3 py-2 text-right tabular-nums">
                <div>{formatCurrency(line.expected_total)}</div>
                <div className="text-[10px] text-[#141414]/45">
                    {formatCurrency(line.expected_unit_cost)} / unit
                </div>
            </td>
            <td className="px-3 py-2">
                <select
                    value={line.vendor ?? ''}
                    disabled={readOnly || isSaving}
                    onChange={(e) => commitPatch({ vendor: e.target.value || null })}
                    className="w-full bg-white border border-[#141414]/25 focus:border-[#e0461e] outline-none px-2 py-1 ff-mono text-xs disabled:opacity-60"
                >
                    <option value="">—</option>
                    {vendors.map((v) => (
                        <option key={v.id} value={v.id}>
                            {v.name}
                        </option>
                    ))}
                </select>
            </td>
            <td className="px-3 py-2">
                <input
                    type="text"
                    value={poDraft}
                    disabled={readOnly || isSaving}
                    onChange={(e) => setPoDraft(e.target.value)}
                    onBlur={() => {
                        if ((line.po_number ?? '') !== poDraft) {
                            void commitPatch({ po_number: poDraft || null });
                        }
                    }}
                    placeholder="—"
                    className="w-28 bg-white border border-[#141414]/25 focus:border-[#e0461e] outline-none px-2 py-1 ff-mono text-xs disabled:opacity-60"
                />
            </td>
            <td className="px-3 py-2">
                <select
                    value={line.status}
                    disabled={readOnly || isSaving}
                    onChange={(e) =>
                        commitPatch({ status: e.target.value as ProcurementStatus })
                    }
                    className={`border border-[#141414]/25 focus:border-[#e0461e] outline-none px-2 py-1 ff-mono text-[10px] uppercase tracking-[0.15em] disabled:opacity-60 ${STATUS_PILL[line.status]}`}
                >
                    {Object.values(ProcurementStatus).map((s) => (
                        <option key={s} value={s}>
                            {STATUS_LABEL[s]}
                        </option>
                    ))}
                </select>
            </td>
            <td className="px-3 py-2 text-right">
                <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={actualDraft}
                    disabled={readOnly || isSaving}
                    onChange={(e) => setActualDraft(e.target.value)}
                    onBlur={() => {
                        const current = line.actual_unit_cost != null ? String(line.actual_unit_cost) : '';
                        if (current !== actualDraft) {
                            const parsed = actualDraft === '' ? null : Number(actualDraft);
                            if (parsed !== null && !Number.isFinite(parsed)) return;
                            void commitPatch({ actual_unit_cost: parsed });
                        }
                    }}
                    placeholder="—"
                    className="w-24 bg-white border border-[#141414]/25 focus:border-[#e0461e] outline-none px-2 py-1 ff-mono text-xs tabular-nums text-right disabled:opacity-60"
                />
                {line.actual_total != null && (
                    <div className="text-[10px] text-[#141414]/45 mt-0.5">
                        total {formatCurrency(line.actual_total)}
                    </div>
                )}
            </td>
            <td className="px-3 py-2">
                <input
                    type="date"
                    value={deliveryDraft}
                    disabled={readOnly || isSaving}
                    onChange={(e) => setDeliveryDraft(e.target.value)}
                    onBlur={() => {
                        if ((line.expected_delivery ?? '') !== deliveryDraft) {
                            void commitPatch({ expected_delivery: deliveryDraft || null });
                        }
                    }}
                    className="bg-white border border-[#141414]/25 focus:border-[#e0461e] outline-none px-2 py-1 ff-mono text-xs disabled:opacity-60"
                />
                {line.received_at && (
                    <div className="text-[10px] text-[#4a6b3d] mt-0.5">
                        rcvd {new Date(line.received_at).toLocaleDateString()}
                    </div>
                )}
            </td>
            <td className="px-3 py-2">
                <input
                    type="text"
                    value={notesDraft}
                    disabled={readOnly || isSaving}
                    onChange={(e) => setNotesDraft(e.target.value)}
                    onBlur={() => {
                        if ((line.notes ?? '') !== notesDraft) {
                            void commitPatch({ notes: notesDraft || null });
                        }
                    }}
                    placeholder="—"
                    className="w-40 bg-white border border-[#141414]/25 focus:border-[#e0461e] outline-none px-2 py-1 ff-mono text-xs disabled:opacity-60"
                />
            </td>
        </tr>
    );
};

interface OrphanTableProps {
    lines: ProcurementLine[];
    vendors: Vendor[];
    isAdmin: boolean;
    savingKey: string | null;
    onRemove: (line: ProcurementLine) => void;
}

const OrphanTable: React.FC<OrphanTableProps> = ({
    lines,
    vendors,
    isAdmin,
    savingKey,
    onRemove,
}) => {
    const vendorName = (id?: string | null) =>
        (id && vendors.find((v) => v.id === id)?.name) || '—';
    return (
        <div className="border border-[#141414]/15 bg-[#141414]/[0.02]">
            <table className="w-full ff-mono text-xs">
                <thead className="border-b border-[#141414]/10">
                    <tr className="text-left">
                        <Th>Equipment</Th>
                        <Th>Vendor</Th>
                        <Th>PO #</Th>
                        <Th>Status</Th>
                        <Th>Notes</Th>
                        <Th className="text-right">Action</Th>
                    </tr>
                </thead>
                <tbody>
                    {lines.map((line) => (
                        <tr key={lineKey(line)} className="border-b border-[#141414]/10 last:border-b-0 text-[#141414]/60">
                            <td className="px-3 py-2">{line.equipment_name}</td>
                            <td className="px-3 py-2">{vendorName(line.vendor)}</td>
                            <td className="px-3 py-2">{line.po_number ?? '—'}</td>
                            <td className="px-3 py-2">
                                <span
                                    className={`inline-block ff-mono text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 ${STATUS_PILL[line.status]}`}
                                >
                                    {STATUS_LABEL[line.status]}
                                </span>
                            </td>
                            <td className="px-3 py-2">{line.notes ?? '—'}</td>
                            <td className="px-3 py-2 text-right">
                                {isAdmin && (
                                    <button
                                        type="button"
                                        disabled={savingKey === lineKey(line)}
                                        onClick={() => onRemove(line)}
                                        className="ff-mono text-[10px] uppercase tracking-wider px-2 py-1 border border-[#141414]/25 text-[#141414]/70 hover:bg-[#c2251a]/10 hover:text-[#c2251a] hover:border-[#c2251a]/40 disabled:opacity-50 transition"
                                    >
                                        Remove
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export default ProcurementPage;
